/**
 * Canvas-aware structural diff between two workflow definitions.
 *
 * Originally inlined in WorkflowVersionsSlideout (RC-117b); promoted to
 * a shared util in RC-118b so useCanvas can re-use the same delta to
 * derive per-op labels for undo/redo without re-implementing the
 * comparison logic.
 *
 * Pure function — no React, no state, no side effects. Returns a
 * structured report consumers can group / render / label freely.
 *
 * Comparisons:
 *   • nodes keyed by id. A node is "changed" when present in both
 *     defs but the JSON-stringified {label, config, type} differs.
 *   • connections keyed by id when present, else by the endpoint
 *     quad `${from}|${fromPort||'default'}|${to}|${toPort||'default'}`
 *     so importer-emitted edges (no id) still diff correctly.
 *
 * Robust against malformed defs: missing nodes/connections arrays
 * coerce to []. Two identical or empty defs produce an all-empty
 * report so consumers can detect "no structural change".
 *
 * @param {{nodes?: object[], connections?: object[]}} defA — "before"
 * @param {{nodes?: object[], connections?: object[]}} defB — "after"
 * @returns {{
 *   nodesAdded: Array<{id, type, label}>,
 *   nodesRemoved: Array<{id, type, label}>,
 *   nodesChanged: Array<{id, type, label, labelChanged: boolean, typeChanged: boolean}>,
 *   edgesAdded: Array<{key, from, to}>,
 *   edgesRemoved: Array<{key, from, to}>
 * }}
 */
export function diffDefinitions(defA, defB) {
  const a = defA || {};
  const b = defB || {};
  const aNodes = Array.isArray(a.nodes) ? a.nodes : [];
  const bNodes = Array.isArray(b.nodes) ? b.nodes : [];
  const aConns = Array.isArray(a.connections) ? a.connections : [];
  const bConns = Array.isArray(b.connections) ? b.connections : [];

  const aNodeMap = new Map(aNodes.filter((n) => n && n.id).map((n) => [n.id, n]));
  const bNodeMap = new Map(bNodes.filter((n) => n && n.id).map((n) => [n.id, n]));

  const nodesAdded = [];
  const nodesRemoved = [];
  const nodesChanged = [];

  bNodeMap.forEach((bn, id) => {
    const an = aNodeMap.get(id);
    if (!an) {
      nodesAdded.push({ id, type: bn.type, label: bn.label || id });
    } else {
      const aSig = JSON.stringify({ label: an.label, config: an.config || {}, type: an.type });
      const bSig = JSON.stringify({ label: bn.label, config: bn.config || {}, type: bn.type });
      if (aSig !== bSig) {
        nodesChanged.push({
          id,
          type: bn.type,
          label: bn.label || id,
          labelChanged: an.label !== bn.label,
          typeChanged: an.type !== bn.type,
        });
      }
    }
  });
  aNodeMap.forEach((an, id) => {
    if (!bNodeMap.has(id)) {
      nodesRemoved.push({ id, type: an.type, label: an.label || id });
    }
  });

  const connKey = (c) => (c && c.id)
    ? `id:${c.id}`
    : `q:${c.from || ''}|${c.fromPort || 'default'}|${c.to || ''}|${c.toPort || 'default'}`;
  const aConnMap = new Map(aConns.filter(Boolean).map((c) => [connKey(c), c]));
  const bConnMap = new Map(bConns.filter(Boolean).map((c) => [connKey(c), c]));

  const edgesAdded = [];
  const edgesRemoved = [];
  bConnMap.forEach((bc, k) => {
    if (!aConnMap.has(k)) edgesAdded.push({ key: k, from: bc.from, to: bc.to });
  });
  aConnMap.forEach((ac, k) => {
    if (!bConnMap.has(k)) edgesRemoved.push({ key: k, from: ac.from, to: ac.to });
  });

  return { nodesAdded, nodesRemoved, nodesChanged, edgesAdded, edgesRemoved };
}

/**
 * RC-118b — Derive a short, operator-readable label for a single
 * mutation from the diff between the BEFORE and AFTER snapshot.
 *
 * Used by useCanvas to tag each undo/redo entry so the toolbar
 * tooltip can show "Undo Add node 'Slack notify'" instead of the
 * bare "Undo". The label is intentionally compact — meant to fit in
 * a button tooltip, not narrate every detail.
 *
 * Categorisation precedence (single-category wins; mixed →
 * "Multiple changes"):
 *   • Empty diff                → "No change"
 *   • 1 add, no other delta     → "Add <type> '<label>'"
 *   • N adds, no other delta    → "Add <N> nodes"
 *   • 1 remove, no other delta  → "Delete <type> '<label>'"
 *   • N removes, no other delta → "Delete <N> nodes"
 *   • 1 change, no other delta  → "Edit '<label>'"
 *   • N changes, no other delta → "Edit <N> nodes"
 *   • 1 edge add                → "Connect <fromShort> → <toShort>"
 *   • 1 edge remove             → "Disconnect <fromShort> → <toShort>"
 *   • 2+ categories             → "Multiple changes"
 *
 * The function NEVER throws — defensive against malformed snapshots
 * because it's called from a useEffect on every state change.
 */
export function deriveOpLabel(beforeDef, afterDef) {
  try {
    const d = diffDefinitions(beforeDef, afterDef);
    const nA = d.nodesAdded.length;
    const nR = d.nodesRemoved.length;
    const nC = d.nodesChanged.length;
    const eA = d.edgesAdded.length;
    const eR = d.edgesRemoved.length;

    if (nA + nR + nC + eA + eR === 0) return 'No change';

    // Count non-empty categories — one wins, two+ collapse to "Multiple changes".
    const categories = [nA > 0, nR > 0, nC > 0, eA > 0, eR > 0].filter(Boolean).length;
    if (categories > 1) return 'Multiple changes';

    if (nA === 1) return `Add ${d.nodesAdded[0].type || 'node'} '${truncate(d.nodesAdded[0].label, 28)}'`;
    if (nA > 1)  return `Add ${nA} nodes`;
    if (nR === 1) return `Delete ${d.nodesRemoved[0].type || 'node'} '${truncate(d.nodesRemoved[0].label, 28)}'`;
    if (nR > 1)  return `Delete ${nR} nodes`;
    if (nC === 1) return `Edit '${truncate(d.nodesChanged[0].label, 28)}'`;
    if (nC > 1)  return `Edit ${nC} nodes`;
    if (eA === 1) return `Connect ${shortId(d.edgesAdded[0].from)} → ${shortId(d.edgesAdded[0].to)}`;
    if (eA > 1)  return `Add ${eA} connections`;
    if (eR === 1) return `Disconnect ${shortId(d.edgesRemoved[0].from)} → ${shortId(d.edgesRemoved[0].to)}`;
    if (eR > 1)  return `Remove ${eR} connections`;
    return 'Edit'; // unreachable per category coverage above, but
                   // a safe fallback if a new diff field gets added
                   // upstream without updating this switch.
  } catch {
    return 'Edit';
  }
}

function truncate(s, max) {
  if (!s) return 'item';
  const t = String(s);
  return t.length <= max ? t : t.slice(0, max) + '…';
}

function shortId(id) {
  if (!id || typeof id !== 'string') return '?';
  // Node ids are `n_<timestamp>_<rand>`; take the suffix as the
  // operator-recognisable hint.
  const parts = id.split('_');
  return parts.length > 1 ? parts[parts.length - 1] : id.slice(0, 6);
}
